/**
 * eventNodeTypes — canonical set of Event Graph node types that can
 * independently START an exec chain: `EventRuntime.fireEvent(type, data)`
 * matches starter nodes by `n.type === type`, so these are the only types
 * ever passed as that `type` argument (see eventStarters.js / EventRuntime.js).
 *
 * Single source of truth for two consumers that used to each keep their own
 * copy in sync by hand:
 *   - EventGraph.jsx — which nodes render in the "MY BLUEPRINT" event list /
 *     get the Events accent color (its own local `EVENT_TYPES` Set, now just
 *     imported from here).
 *   - execReachability.js — which nodes count as valid roots when asking "is
 *     node X wired to anything live" (ENG-981 `headTrackedCamera` disarm
 *     fix): a node fed from a real event root is live regardless of which
 *     event actually fires a given pass; a node fed only from an
 *     orphaned/never-fired action node is not.
 *
 * Pure data, no imports — safe to import under plain `node --test` (unlike
 * EventGraph.jsx, which is a React/JSX component and transitively pulls in
 * browser-only modules).
 */
export const EVENT_NODE_TYPES = new Set([
  'beginPlay', // backward-compat (Wave 2B: retyped to onConstruct at every hydration boundary; kept so an unmigrated in-memory graph still counts it as a root)
  'onConstruct',
  'onWSConnected',
  'onWSDisconnected',
  'onNewResponse',
  'onPrefabTrigger',
  'onResponseComplete',
  'onGesture',
  'onAnimation', // backward-compat
  'onSentenceStarted',
  'onSentenceCompleted',
  'onLastSentenceStarted',
  'onAllSentencesPlayed',
  'onSentenceDataReady',
  'onFirstSentenceReady',
  'onTextInput',
  'onUserTyping',
  'onUserTypingCancelled',
  'onSession',
  'onMetrics',
  'onSpeechStart',
  'onSpeechEnd',
  'onBargeIn',
  'onSentenceWaiting',
  'onServerError',
  'inputKey',
  'onPointerDown',
  'onPointerUp',
  'onPointerDrag',
  'onWheel',
  'onKeyDown',
  'onKeyUp',
  'onMoveCompleted',
  // ENG-1062: a named, user-defined event. Fired BY NAME via
  // EventRuntime.fireCustomEvent(name) — most notably from a state-machine
  // transition's Actions — not by type through fireEvent. Listed here so it
  // counts as a valid exec-chain ROOT for execReachability.js and gets the
  // Events accent / MY-BLUEPRINT listing in EventGraph.jsx.
  'customEvent',
])
